import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PenSquare, Edit3, Eye, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Button } from './ui/button.jsx';
import OrnateDivider from './OrnateDivider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  draft: { label: 'Draft', color: 'bg-slate-500/15 text-slate-300 border-slate-400/30' },
  pending_review: { label: 'Pending', color: 'bg-amber-500/15 text-amber-300 border-amber-400/30' },
  published: { label: 'Published', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' },
  rejected: { label: 'Rejected', color: 'bg-rose-500/15 text-rose-300 border-rose-400/30' },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const MyBlogPosts = () => {
  const [role, setRole] = useState(null);
  const [posts, setPosts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const navigate = useNavigate();

  const authHeader = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  useEffect(() => {
    axios.get(`${API}/blog/my-role`, { headers: authHeader() })
      .then((r) => setRole(r.data.role))
      .catch(() => setRole('reader'));
  }, []);

  useEffect(() => {
    if (!role || role === 'reader') return;
    axios.get(`${API}/blog/my-posts`, { headers: authHeader() })
      .then((r) => setPosts(r.data || []))
      .catch(() => setPosts([]));
  }, [role]);

  if (!role || role === 'reader') return null;

  return (
    <div className="mt-12" data-testid="dashboard-my-posts">
      <OrnateDivider label="My Posts" variant="diamond" className="mb-6 max-w-lg mx-auto" />
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-heading text-primary uppercase tracking-wider">My Posts</h2>
        <Button onClick={() => navigate('/blog/new')} className="ornate-button" data-testid="dashboard-new-post-btn">
          <PenSquare size={16} className="mr-2" />New Post
        </Button>
      </div>

      {posts.length === 0 ? (
        <div className="card-mystical rounded-2xl p-10 text-center text-primary/60">
          You haven't written any posts yet. Click <strong>New Post</strong> to start.
        </div>
      ) : (
        <div className="card-mystical rounded-2xl divide-y divide-primary/10">
          {posts.map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.draft;
            const isOpen = !!expanded[p.id];
            return (
              <div key={p.id} className="p-4 sm:p-5" data-testid={`my-post-row-${p.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-[0.2em] ${meta.color}`}>
                        {meta.label}
                      </span>
                      <span className="text-xs text-primary/60">Updated {fmt(p.updated_at)}</span>
                    </div>
                    <div className="text-primary font-medium">{p.title}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/blog/edit/${p.slug}`)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-primary/30 rounded-lg text-primary/90 hover:bg-primary/10 transition"
                      data-testid={`my-post-edit-${p.id}`}
                    >
                      <Edit3 size={14} /> Edit
                    </button>
                    {p.status === 'published' && (
                      <button
                        type="button"
                        onClick={() => navigate(`/blog/${p.slug}`)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-primary/30 rounded-lg text-primary/90 hover:bg-primary/10 transition"
                        data-testid={`my-post-preview-${p.id}`}
                      >
                        <Eye size={14} /> Preview
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
                        try {
                          await axios.delete(`${API}/blog/${p.id}`, { headers: authHeader() });
                          setPosts((prev) => prev.filter((x) => x.id !== p.id));
                        } catch (_e) { /* ignore */ }
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-rose-400/30 rounded-lg text-rose-300 hover:bg-rose-500/10 transition"
                      data-testid={`my-post-delete-${p.id}`}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>

                {p.status === 'rejected' && p.rejection_feedback && (
                  <>
                    <button
                      type="button"
                      onClick={() => setExpanded((e) => ({ ...e, [p.id]: !isOpen }))}
                      className="mt-3 inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200"
                      data-testid={`my-post-feedback-toggle-${p.id}`}
                    >
                      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {isOpen ? 'Hide feedback' : 'View feedback'}
                    </button>
                    {isOpen && (
                      <div className="mt-2 p-3 rounded-lg border border-rose-400/30 bg-rose-500/5 text-sm text-rose-100">
                        {p.rejection_feedback}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyBlogPosts;
